import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Notice } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { SPORT_LABEL } from "../lib/sport.js";
import { fetchPastTournaments, type PastTournament } from "../lib/tournaments.js";
import { TournamentBanner } from "./Tournaments.js";

/**
 * Tournaments that have been finished, which the Events list no longer shows.
 *
 * For organisers: it is how somebody finds last month's tournament to look at who won, or to
 * reopen. Read-only on purpose — a row is the banner and what was won, with no Register and no
 * way to change anything. Reopening stays on a tournament's own manage screen, for admins.
 *
 * Sending everybody else back is signposting, not the lock. A finished tournament is readable by
 * its link to anyone who can see it, so nothing here widens access; private ones are hidden
 * from the uninvited by the same policies as everywhere, because this reads through them.
 */
export function PastTournaments() {
  const { member } = useAuth();
  const allowed = hasRole(member, "organiser");

  const { data, isPending, error } = useQuery({
    queryKey: ["past-tournaments"],
    queryFn: fetchPastTournaments,
    enabled: allowed,
  });

  if (!allowed) return <Navigate to="/events" replace />;

  const past = data ?? [];

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to="/events" className="micro mb-4 inline-block underline">
        ← All events
      </Link>
      <PageHeader eyebrow={SPORT_LABEL} title="Past tournaments" />

      {isPending ? <p className="micro">Loading…</p> : null}
      {error ? (
        <Notice tone="attention">Couldn&rsquo;t load past tournaments. Try again.</Notice>
      ) : null}

      {!isPending && !error && past.length === 0 ? (
        <p className="my-4 text-sm text-ink-muted">
          No past tournaments yet. A tournament appears here once it is finished.{" "}
          <Link to="/events" className="underline">
            Back to events
          </Link>
        </p>
      ) : null}

      {past.map((entry) => (
        <PastRow key={entry.tournament.id} entry={entry} />
      ))}
    </main>
  );
}

function PastRow({ entry }: { entry: PastTournament }) {
  const { tournament, eventCount, mvps } = entry;

  return (
    <article className="mb-8" data-testid="past-tournament">
      <Link to={`/tournaments/${tournament.id}`} className="block">
        <TournamentBanner
          tournament={tournament}
          finished
          heading="h2"
          note={`${eventCount} ${eventCount === 1 ? "event" : "events"}`}
        />
      </Link>
      {/* Nothing at all when nobody was named: an auction tournament has no matches yet, and
          a line saying so would read as something gone wrong. */}
      {mvps.length > 0 ? (
        <ul className="mt-2">
          {mvps.map((line) => (
            <li
              key={line.eventName}
              className="flex min-h-[36px] items-baseline gap-3 border-b py-1.5 text-sm"
              style={{ borderColor: "var(--color-line-muted)" }}
            >
              <span className="font-mono shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em]">
                MVP
              </span>
              <span className="min-w-0 flex-1 truncate text-ink-muted">{line.eventName}</span>
              <span className="font-semibold">{line.names.join(", ")}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}
